/**
 * City environment state shared by all city material plugins (P1-T06).
 * One mutable singleton updated once per frame from main.ts; plugins read it
 * in bindForSubMesh. Keeping it outside Babylon lets Vitest check the easing.
 */

export interface EnvTargets {
  rain: number; // 0..1
  snow: number; // 0..1
  wind: number; // 0..~2
  night: number; // 0..1 (1 = full dark)
  time: number; // seconds of world day for shader animation
}

/** Pure step: wet and snowCover ease toward targets (prototype values). */
export function easeEnv(
  prev: { wet: number; snow: number },
  t: EnvTargets,
  dtS: number,
): { wet: number; snow: number } {
  const k = Math.min(1, dtS * 0.35);
  return {
    wet: prev.wet + ((t.rain > 0.05 ? Math.min(1, t.rain * 1.3) : 0) - prev.wet) * k,
    snow: prev.snow + (t.snow * 0.9 - prev.snow) * k * 0.5,
  };
}

export const CityEnv = {
  wet: 0,
  snow: 0,
  wind: 0.45,
  night: 0,
  time: 0,
  /** test hook: pin wetness for shader-diff probes (null = auto ease) */
  wetOverride: null as number | null,
  update(t: EnvTargets, dtS: number): void {
    const e = easeEnv({ wet: this.wet, snow: this.snow }, t, dtS);
    this.wet = this.wetOverride ?? e.wet;
    this.snow = e.snow;
    this.wind = t.wind;
    this.night = t.night;
    this.time = t.time;
  },
};
